import { useEffect, useMemo, useState } from 'react';
import { listPrompts, type PromptListRow, type PromptType } from './api/promptsApi';

export type PromptFilter =
  | { kind: 'bin-fullness' } // Fullness prompts, excluding wall_*
  | { kind: 'count' }
  | { kind: 'wall' }; // any version starting with "wall_"

export interface PromptVersionPickerProps {
  label: string;
  filter: PromptFilter;
  /**
   * localStorage key for the user's override. When absent (null) the picker
   * defaults to the server-side active prompt of the matching type. When the
   * user picks "Server default" we clear the key and pass undefined upstream
   * so the backend applies its usual resolution.
   */
  storageKey: string;
  value: string | undefined;
  onChange: (version: string | undefined) => void;
  disabled?: boolean;
  testId?: string;
}

// Small hook so every mount picks up the same prompt list without hammering
// the API. In-flight promise is de-duped; result is cached for the tab
// lifetime. Refresh requires a full reload, which is fine — prompt rows are
// seeded, not user-editable through this feature.
let cache: PromptListRow[] | null = null;
let inflight: Promise<PromptListRow[]> | null = null;

async function loadPromptsCached(): Promise<PromptListRow[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = listPrompts()
    .then((rows) => {
      cache = rows;
      return rows;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

function matchesFilter(row: PromptListRow, filter: PromptFilter): boolean {
  const isWall = row.version.startsWith('wall_');
  switch (filter.kind) {
    case 'bin-fullness':
      return row.prompt_type === 'Fullness' && !isWall;
    case 'count':
      return row.prompt_type === 'Count';
    case 'wall':
      return isWall;
  }
}

function activeTypeFor(filter: PromptFilter): PromptType {
  return filter.kind === 'count' ? 'Count' : 'Fullness';
}

export function PromptVersionPicker({
  label,
  filter,
  storageKey,
  value,
  onChange,
  disabled,
  testId,
}: PromptVersionPickerProps) {
  const [rows, setRows] = useState<PromptListRow[] | null>(cache);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (rows) return;
    let cancelled = false;
    loadPromptsCached()
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const filtered = useMemo(() => (rows ?? []).filter((r) => matchesFilter(r, filter)), [rows, filter]);
  const activeVersion = useMemo(
    () => (rows ?? []).find((r) => r.is_active && r.prompt_type === activeTypeFor(filter))?.version,
    [rows, filter]
  );

  // Selecting "" (default) clears storage and tells upstream to send no override.
  const handleChange = (next: string) => {
    if (next === '') {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
      onChange(undefined);
      return;
    }
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      /* ignore */
    }
    onChange(next);
  };

  return (
    <label className="flex items-center gap-2 text-sm text-gray-300" data-testid={testId}>
      <span className="whitespace-nowrap">{label}</span>
      <select
        value={value ?? ''}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled || !rows || loadError !== null}
        className="rounded border border-gray-600 bg-gray-800 px-2 py-1 disabled:opacity-50"
      >
        <option value="">
          {activeVersion ? `Server default (${activeVersion})` : 'Server default'}
        </option>
        {filtered.map((r) => (
          <option key={r.id} value={r.version}>
            {r.version}
            {r.is_active ? ' · active' : ''}
          </option>
        ))}
      </select>
      {loadError && <span className="text-xs text-red-400">prompts unavailable</span>}
    </label>
  );
}

/**
 * Read the persisted prompt override for a picker. Returns undefined when
 * nothing is set OR the stored value has since been removed from the server
 * (validated against the current prompt list — no dead versions on the wire).
 */
export function readStoredPromptVersion(storageKey: string): string | undefined {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ?? undefined;
  } catch {
    return undefined;
  }
}
